import React, { useState, useEffect, useMemo } from 'react';
import { message, Pagination, Card, Select, DatePicker, Button, Table, Input, Space, App, Typography } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../../theme';
import dayjs, { Dayjs } from 'dayjs';
import { SearchOutlined, DatabaseOutlined, CheckCircleOutlined, WarningOutlined, PlusOutlined, UpOutlined, DownOutlined, EditOutlined } from '@ant-design/icons';
import { ExportExcelButton, ExportPDFButton, ColumnManagerDropdown } from '../common';
import { useColumnManager } from '../../hooks';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import type { ColumnsType } from 'antd/es/table';

const { Text, Title } = Typography;

interface User {
  id_empresa: number;
  id_sucursal: number;
  tipo_usuario?: string;
}

interface Producto {
  id: number;
  descripcion: string;
}

interface TicketBalanza {
  numero_ticket: number;
  compra_servicio: string; // Ahora contiene "Compra", "Servicio" o valor original
  kilos_netos: number;
  cliente: string;
  productor: string;
  establecimiento?: string;
  numero_remito?: string;
  dtv?: string;
}

interface Fardo {
  numero_fardo: number;
  cantidad: number;
  peso: number;
}

interface ResultadoProduccion {
  id: number;
  numero_produccion: number;
  fecha: string;
  fecha_hora_inicio: string;
  fecha_hora_final: string;
  demora: number;
  tickets_balanza: TicketBalanza[];
  total_kilos_netos_desmotados: number;
  total_cantidad_fardos: number;
  total_peso_fardos: number;
  kg_fibra: number;
  rinde_fibra: number;
  kilos_semilla: number;
  rinde_semilla: number;
  resto_positivo?: number;
  resto_negativo?: number;
  ritmo_desmote_kg_hora?: number;
  ritmo_desmote_q_hora?: number;
  cliente: string;
  productor: string;
  producto: string;
}

const ListadoProduccion: React.FC = () => {
  const { message: messageApi } = App.useApp();
  const { getAccessTokenSilently, user } = useAuth0();
  const navigate = useNavigate();
  const [productos, setProductos] = useState<Producto[]>([]);
  const [productoSeleccionado, setProductoSeleccionado] = useState<string>('');
  const [fechaDesde, setFechaDesde] = useState<string>('');
  const [fechaHasta, setFechaHasta] = useState<string>('');
  const [filtroCompraServicio, setFiltroCompraServicio] = useState<string>('');
  const [resultados, setResultados] = useState<ResultadoProduccion[]>([]);
  const [loading, setLoading] = useState(false);
  const [produccionSeleccionada, setProduccionSeleccionada] = useState<number | null>(null);
  const [globalSearch, setGlobalSearch] = useState<string>('');
  const [showStatsExpanded, setShowStatsExpanded] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Lista de todas las columnas disponibles (keys)
  const allColumnKeys = [
    'numero_produccion',
    'fecha',
    'fecha_hora_inicio',
    'fecha_hora_final',
    'demora',
    'numero_ticket',
    'numero_remito',
    'dtv',
    'establecimiento',
    'compra_servicio',
    'cliente',
    'productor',
    'kilos_netos',
    'total_cantidad_fardos',
    'total_peso_fardos',
    'kg_fibra',
    'rinde_fibra',
    'kilos_semilla',
    'rinde_semilla',
    'resto_positivo',
    'resto_negativo',
    'ritmo_desmote_kg_hora',
    'ritmo_desmote_q_hora',
    'acciones'
  ];

  // Nombres amigables para las columnas
  const columnNames: Record<string, string> = {
    'numero_produccion': 'Nro. Producción',
    'fecha': 'Fecha',
    'fecha_hora_inicio': 'Inicio',
    'fecha_hora_final': 'Final',
    'demora': 'Demora (min)',
    'numero_ticket': 'Nro. Ticket',
    'numero_remito': 'Nro. Remito',
    'dtv': 'DTV',
    'establecimiento': 'Establecimiento',
    'compra_servicio': 'Compra/Servicio',
    'cliente': 'Cliente',
    'productor': 'Productor',
    'kilos_netos': 'Kilos Netos',
    'total_cantidad_fardos': 'Cant. Fardos',
    'total_peso_fardos': 'Peso Fardos',
    'kg_fibra': 'Kg. Fibra',
    'rinde_fibra': 'Rinde Fibra %',
    'kilos_semilla': 'Kilos Semilla',
    'rinde_semilla': 'Rinde Semilla %',
    'resto_positivo': 'Resto (+)',
    'resto_negativo': 'Resto (-)',
    'ritmo_desmote_kg_hora': 'Ritmo (Kg Fib/H)',
    'ritmo_desmote_q_hora': 'Ritmo (Q Fard/H)',
    'acciones': 'Acciones'
  };

  // Visibilidad + orden de columnas (persistido) — reemplaza el manejo manual de antes.
  // Los cambios (tildar/destildar, arrastrar) se aplican al instante, sin botón "Aplicar".
  const columnManager = useColumnManager({
    storageKey: 'listado_produccion',
    allKeys: allColumnKeys,
    requiredKeys: ['acciones'],
  });

  // Obtener fecha de hace 30 días (default del rango de fechas)
  const obtenerFechaDesde30Dias = (): string => {
    const fecha = new Date();
    fecha.setDate(fecha.getDate() - 30);
    return fecha.toISOString().split('T')[0];
  };

  // Obtener fecha actual
  const obtenerFechaActual = (): string => {
    return new Date().toISOString().split('T')[0];
  };

  // Cargar productos tipo=2 al inicio
  useEffect(() => {
    const fetchProductos = async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${process.env.REACT_APP_BALANZA_URL}/productos/tipo/2`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        
        const data = await response.json();
        console.log('Respuesta productos completa:', data);
        
        // Si data es directamente el array de productos
        if (Array.isArray(data)) {
          console.log('Data es un array:', data);
          setProductos(data);
        }
        // Si data tiene la estructura { status, data }
        else if (data.status === 'success' && Array.isArray(data.data)) {
          console.log('Data está dentro de data.data:', data.data);
          setProductos(data.data);
        }
        // Si data tiene otra estructura, mostrarla
        else {
          console.log('Estructura de data inesperada:', typeof data, data);
          messageApi.error('Error en el formato de la respuesta');
        }
      } catch (error) {
        console.error('Error completo:', error);
        messageApi.error('ERROR AL CARGAR LOS PRODUCTOS');
      }
    };

    fetchProductos();
    
    // Establecer fechas iniciales
    setFechaDesde(obtenerFechaDesde30Dias());
    setFechaHasta(obtenerFechaActual());
  }, [getAccessTokenSilently]);

  const handleSeleccionProduccion = (idProduccion: number) => {
    setProduccionSeleccionada(idProduccion);
    console.log('Producción seleccionada:', idProduccion);
  };

  const handlePageChange = (page: number, size?: number) => {
    setCurrentPage(page);
    if (size) {
      setPageSize(size);
      setCurrentPage(1); // Resetear a página 1 cuando cambia el tamaño
    }
  };

  const handleProduccionManual = () => {
    navigate('/A_Produccion');
  };

  const handleProduccionAutomatica = () => {
    navigate('/previoDesmote');
  };

  const handleEmision = () => {
    if (produccionSeleccionada) {
      const userMetadata = user as unknown as User;
      const id_empresa = userMetadata?.id_empresa || 1; // Default to 1 if not available
      const id_sucursal = userMetadata?.id_sucursal || 1; // Default to 1 if not available
      console.log('varios', id_empresa, id_sucursal);
      // Esto debería venir del contexto de la aplicación
      navigate(`/EmisionProduccion?id_empresa=${id_empresa}&id_sucursal=${id_sucursal}&id_produccion=${produccionSeleccionada}`);
    }
  };

  const handleEditarProduccion = (idProduccion: number) => {
    const userMetadata = user as unknown as User;
    const id_empresa = userMetadata?.id_empresa || 1;
    const id_sucursal = userMetadata?.id_sucursal || 1;
    navigate(`/produccion/editar/${id_empresa}/${id_sucursal}/${idProduccion}`);
  };

  // Función para resaltar términos en el texto
  const highlightText = (text: string, searchTerm: string) => {
    if (!searchTerm.trim() || !text) return text;
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} style={{
          backgroundColor: theme.colors.warningLight,
          padding: '2px 4px',
          borderRadius: '4px',
          fontWeight: 600
        }}>
          {part}
        </mark>
      ) : part
    );
  };

  // Filtrar datos según búsqueda global
  const filteredData = useMemo(() => {
    if (!globalSearch.trim()) {
      return resultados;
    }

    const searchLower = globalSearch.toLowerCase();
    return resultados.filter(record => {
      return (
        record.numero_produccion.toString().includes(searchLower) ||
        record.fecha.toLowerCase().includes(searchLower) ||
        record.fecha_hora_inicio.toLowerCase().includes(searchLower) ||
        record.fecha_hora_final.toLowerCase().includes(searchLower) ||
        record.demora.toString().includes(searchLower) ||
        (record.cliente || '').toLowerCase().includes(searchLower) ||
        (record.productor || '').toLowerCase().includes(searchLower) ||
        (record.producto || '').toLowerCase().includes(searchLower) ||
        record.total_kilos_netos_desmotados.toString().includes(searchLower) ||
        record.total_cantidad_fardos.toString().includes(searchLower) ||
        record.total_peso_fardos.toString().includes(searchLower) ||
        record.tickets_balanza.some(t =>
          t.numero_ticket.toString().includes(searchLower) ||
          (t.cliente || '').toLowerCase().includes(searchLower) ||
          (t.productor || '').toLowerCase().includes(searchLower) ||
          t.compra_servicio.toLowerCase().includes(searchLower) ||
          (t.establecimiento || '').toLowerCase().includes(searchLower) ||
          (t.numero_remito || '').toLowerCase().includes(searchLower) ||
          (t.dtv || '').toLowerCase().includes(searchLower)
        )
      );
    });
  }, [resultados, globalSearch]);

  // Calcular estadísticas mejoradas
  const estadisticas = useMemo(() => {
    const total = filteredData.length;
    const totalKilos = filteredData.reduce((acc, p) => acc + (p.total_kilos_netos_desmotados || 0), 0);
    const totalFardos = filteredData.reduce((acc, p) => acc + (p.total_cantidad_fardos || 0), 0);
    const totalPesoFardos = filteredData.reduce((acc, p) => acc + (p.total_peso_fardos || 0), 0);
    const promedioDemora = total > 0 
      ? Math.round(filteredData.reduce((acc, p) => acc + (p.demora || 0), 0) / total)
      : 0;

    // Top 3 productos
    const productosCount = filteredData.reduce((acc, record) => {
      const prod = record.producto || 'Sin producto';
      acc[prod] = (acc[prod] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    const topProductos = Object.entries(productosCount)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3)
      .map(([nombre, count]) => ({ nombre, count }));

    return {
      total,
      totalKilos,
      totalFardos,
      totalPesoFardos,
      promedioDemora,
      topProductos
    };
  }, [filteredData]);

  // Mapa de columnas exportables: key → { label, getValue }
  const exportColumnMap: Record<string, { label: string; getValue: (r: ResultadoProduccion) => string | number }> = {
    'numero_produccion': { label: 'Nro. Producción', getValue: r => r.numero_produccion },
    'fecha':             { label: 'Fecha',            getValue: r => r.fecha },
    'fecha_hora_inicio': { label: 'Inicio',           getValue: r => r.fecha_hora_inicio },
    'fecha_hora_final':  { label: 'Final',            getValue: r => r.fecha_hora_final },
    'demora':            { label: 'Demora (min)',      getValue: r => r.demora },
    'numero_ticket':     { label: 'Nro. Ticket',      getValue: r => r.tickets_balanza?.[0]?.numero_ticket ?? '' },
    'numero_remito':     { label: 'Nro. Remito',      getValue: r => r.tickets_balanza?.[0]?.numero_remito ?? '' },
    'dtv':               { label: 'DTV',              getValue: r => r.tickets_balanza?.[0]?.dtv ?? '' },
    'establecimiento':   { label: 'Establecimiento',  getValue: r => r.tickets_balanza?.[0]?.establecimiento ?? '' },
    'compra_servicio':   { label: 'Compra/Servicio',  getValue: r => r.tickets_balanza?.[0]?.compra_servicio ?? '' },
    'cliente':           { label: 'Cliente',          getValue: r => r.cliente || '' },
    'productor':         { label: 'Productor',        getValue: r => r.productor || '' },
    'kilos_netos':       { label: 'Kilos Netos',      getValue: r => r.total_kilos_netos_desmotados || 0 },
    'total_cantidad_fardos': { label: 'Cant. Fardos', getValue: r => r.total_cantidad_fardos || 0 },
    'total_peso_fardos': { label: 'Peso Fardos',      getValue: r => r.total_peso_fardos || 0 },
    'kg_fibra':          { label: 'Kg. Fibra',        getValue: r => r.kg_fibra || 0 },
    'rinde_fibra':       { label: 'Rinde Fibra %',    getValue: r => `${(r.rinde_fibra || 0).toFixed(2)}%` },
    'kilos_semilla':     { label: 'Kilos Semilla',    getValue: r => r.kilos_semilla || 0 },
    'rinde_semilla':     { label: 'Rinde Semilla %',  getValue: r => `${(r.rinde_semilla || 0).toFixed(2)}%` },
    'resto_positivo':    { label: 'Resto (+)',         getValue: r => (r.resto_positivo || 0).toFixed(2) },
    'resto_negativo':    { label: 'Resto (-)',         getValue: r => (r.resto_negativo || 0).toFixed(2) },
    'ritmo_desmote_kg_hora': { label: 'Ritmo (Kg Fib/H)', getValue: r => r.ritmo_desmote_kg_hora || 0 },
    'ritmo_desmote_q_hora':  { label: 'Ritmo (Q Fard/H)', getValue: r => r.ritmo_desmote_q_hora || 0 },
  };

  // Columnas visibles para exportar, en el mismo orden que se ve en la tabla (sin 'acciones')
  const exportKeys = columnManager.orderedVisibleKeys.filter(k => k !== 'acciones');

  // Función para exportar a Excel
  const handleExportExcel = () => {
    try {
      const worksheet = XLSX.utils.json_to_sheet(
        filteredData.map(record => {
          const row: Record<string, any> = {};
          exportKeys.forEach(key => {
            const col = exportColumnMap[key];
            if (col) row[col.label] = col.getValue(record);
          });
          return row;
        })
      );
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Producción');
      XLSX.writeFile(workbook, `produccion_${new Date().toISOString().split('T')[0]}.xlsx`);
      messageApi.success('Archivo Excel exportado correctamente');
    } catch (error) {
      console.error('Error al exportar Excel:', error);
      messageApi.error('Error al exportar el archivo Excel');
    }
  };

  // Función para exportar a PDF
  const handleExportPDF = () => {
    try {
      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text('Listado de Producción', 14, 15);
      doc.setFontSize(10);
      doc.text(`Fecha: ${new Date().toLocaleDateString('es-AR')}`, 14, 22);
      doc.text(`Total de registros: ${filteredData.length}`, 14, 28);

      const headers = exportKeys.map(k => exportColumnMap[k]?.label || k);
      const tableData = filteredData.map(record =>
        exportKeys.map(key => String(exportColumnMap[key]?.getValue(record) ?? ''))
      );

      (doc as any).autoTable({
        head: [headers],
        body: tableData,
        startY: 35,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [45, 92, 146] }
      });

      doc.save(`produccion_${new Date().toISOString().split('T')[0]}.pdf`);
      messageApi.success('Archivo PDF exportado correctamente');
    } catch (error) {
      console.error('Error al exportar PDF:', error);
      messageApi.error('Error al exportar el archivo PDF');
    }
  };

  const handleBusqueda = async () => {
    if (!productoSeleccionado) {
      messageApi.warning('Por favor seleccione un producto');
      return;
    }

    if (!fechaDesde || !fechaHasta) {
      messageApi.warning('Por favor seleccione las fechas');
      return;
    }

    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const userMetadata = user as unknown as User;
      const id_empresa = userMetadata?.id_empresa || 1;
      console.log('DEBUG listado_produccion - user object:', user);
      console.log('DEBUG listado_produccion - id_empresa enviado:', id_empresa);

      const response = await fetch(
        `${process.env.REACT_APP_PRODUCCION_URL}/listado_produccion/${id_empresa}/${productoSeleccionado}/${fechaDesde}/${fechaHasta}/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();
      
      if (data.status === 'success') {
        console.log('Datos de producción:', data.data);
        let resultadosFiltrados = data.data;
        
        // Aplicar filtro de Compra/Servicio si está seleccionado
        if (filtroCompraServicio) {
          resultadosFiltrados = resultadosFiltrados.filter((item: ResultadoProduccion) => {
            return item.tickets_balanza?.some(ticket =>
              ticket.compra_servicio === filtroCompraServicio
            );
          });
        }

        setResultados(resultadosFiltrados);
        messageApi.success(data.message);
      } else {
        messageApi.error(data.message || 'Error al obtener datos de producción');
        setResultados([]);
      }
    } catch (error) {
      console.error('Error al buscar producción:', error);
      messageApi.error('Error al buscar producción');
      setResultados([]);
    } finally {
      setLoading(false);
    }
  };

  // Preparar datos expandidos para la tabla
  const expandedData = useMemo(() => {
    return filteredData.flatMap((item) => {
      const rows = [];
      // Fila principal
      rows.push({
        key: `main-${item.id}`,
        isMain: true,
        ...item,
        primerTicket: item.tickets_balanza && item.tickets_balanza.length > 0 ? item.tickets_balanza[0] : null
      });
      // Filas de tickets adicionales
      if (item.tickets_balanza && item.tickets_balanza.length > 1) {
        item.tickets_balanza.slice(1).forEach((ticket, ticketIndex) => {
          rows.push({
            key: `ticket-${item.id}-${ticketIndex}`,
            isMain: false,
            ...item,
            ticket
          });
        });
      }
      return rows;
    });
  }, [filteredData]);

  // Paginar solo las filas principales (producciones)
  const paginatedData = useMemo(() => {
    const mainRows = expandedData.filter(row => row.isMain);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedMainRows = mainRows.slice(startIndex, endIndex);
    
    // Incluir las filas de tickets asociadas a las producciones paginadas
    const paginatedMainIds = new Set(paginatedMainRows.map(row => row.id));
    return expandedData.filter(row => paginatedMainIds.has(row.id));
  }, [expandedData, currentPage, pageSize]);

  const columns: ColumnsType<any> = [
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Nro. Producción</span>,
      dataIndex: 'numero_produccion',
      key: 'numero_produccion',
      width: 120,
      render: (text, record) => record.isMain ? (
        <span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>{text}</span>
      ) : null
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Fecha</span>,
      dataIndex: 'fecha',
      key: 'fecha',
      width: 100,
      render: (text, record) => record.isMain ? (
        <span style={{ color: theme.colors.textSecondary }}>{text}</span>
      ) : null
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Inicio</span>,
      dataIndex: 'fecha_hora_inicio',
      key: 'fecha_hora_inicio',
      width: 120,
      render: (text, record) => record.isMain ? (
        <span style={{ color: theme.colors.textSecondary }}>{text}</span>
      ) : null
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Final</span>,
      dataIndex: 'fecha_hora_final',
      key: 'fecha_hora_final',
      width: 120,
      render: (text, record) => record.isMain ? (
        <span style={{ color: theme.colors.textSecondary }}>{text}</span>
      ) : null
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Demora (min)</span>,
      dataIndex: 'demora',
      key: 'demora',
      width: 100,
      align: 'right' as const,
      render: (text, record) => record.isMain ? (
        <span style={{ color: theme.colors.textSecondary }}>{text}</span>
      ) : null
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Nro. Ticket</span>,
      key: 'numero_ticket',
      width: 100,
      render: (_, record) => {
        if (record.isMain) {
          return record.primerTicket ? (
            <span style={{ color: theme.colors.textSecondary }}>
              {globalSearch ? highlightText(record.primerTicket.numero_ticket.toString(), globalSearch) : record.primerTicket.numero_ticket}
            </span>
          ) : '-';
        }
        return record.ticket ? (
          <span style={{ color: theme.colors.textSecondary, fontStyle: 'italic' }}>
            {globalSearch ? highlightText(record.ticket.numero_ticket.toString(), globalSearch) : record.ticket.numero_ticket}
          </span>
        ) : null;
      }
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Nro. Remito</span>,
      key: 'numero_remito',
      width: 120,
      render: (_, record) => {
        const remito = record.isMain
          ? (record.primerTicket?.numero_remito || '')
          : record.ticket?.numero_remito;
        if (!remito) return record.isMain ? '-' : null;
        return (
          <span style={{ color: theme.colors.textSecondary, fontStyle: record.isMain ? 'normal' : 'italic' }}>
            {globalSearch ? highlightText(remito, globalSearch) : remito}
          </span>
        );
      }
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>DTV</span>,
      key: 'dtv',
      width: 100,
      render: (_, record) => {
        const dtv = record.isMain
          ? (record.primerTicket?.dtv || '')
          : record.ticket?.dtv;
        if (!dtv) return record.isMain ? '-' : null;
        return (
          <span style={{ color: theme.colors.textSecondary, fontStyle: record.isMain ? 'normal' : 'italic' }}>
            {globalSearch ? highlightText(dtv, globalSearch) : dtv}
          </span>
        );
      }
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Establecimiento</span>,
      key: 'establecimiento',
      width: 150,
      render: (_, record) => {
        const est = record.isMain
          ? (record.primerTicket?.establecimiento || '')
          : record.ticket?.establecimiento;
        if (!est) return record.isMain ? '-' : null;
        return (
          <span style={{ color: theme.colors.textSecondary, fontStyle: record.isMain ? 'normal' : 'italic' }}>
            {globalSearch ? highlightText(est, globalSearch) : est}
          </span>
        );
      }
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Compra/Servicio</span>,
      key: 'compra_servicio',
      width: 120,
      render: (_, record) => {
        if (record.isMain) {
          return record.primerTicket ? (
            <span style={{ color: theme.colors.textSecondary }}>
              {globalSearch ? highlightText(record.primerTicket.compra_servicio, globalSearch) : record.primerTicket.compra_servicio}
            </span>
          ) : '-';
        }
        return record.ticket ? (
          <span style={{ color: theme.colors.textSecondary, fontStyle: 'italic' }}>
            {globalSearch ? highlightText(record.ticket.compra_servicio, globalSearch) : record.ticket.compra_servicio}
          </span>
        ) : null;
      }
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Cliente</span>,
      key: 'cliente',
      width: 150,
      render: (_, record) => {
        const cliente = record.isMain 
          ? (record.primerTicket?.cliente || record.cliente)
          : record.ticket?.cliente;
        if (!cliente) return record.isMain ? '-' : null;
        return (
          <span style={{ color: theme.colors.textSecondary, fontStyle: record.isMain ? 'normal' : 'italic' }}>
            {globalSearch ? highlightText(cliente, globalSearch) : cliente}
          </span>
        );
      }
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Productor</span>,
      key: 'productor',
      width: 150,
      render: (_, record) => {
        const productor = record.isMain 
          ? (record.primerTicket?.productor || record.productor)
          : record.ticket?.productor;
        if (!productor) return record.isMain ? '-' : null;
        return (
          <span style={{ color: theme.colors.textSecondary, fontStyle: record.isMain ? 'normal' : 'italic' }}>
            {globalSearch ? highlightText(productor, globalSearch) : productor}
          </span>
        );
      }
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Kilos Netos</span>,
      key: 'kilos_netos',
      width: 100,
      align: 'right' as const,
      render: (_, record) => {
        if (record.isMain) {
          return (
            <span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>
              {(record.total_kilos_netos_desmotados || 0).toFixed(2)}
            </span>
          );
        }
        return record.ticket ? (
          <span style={{ color: theme.colors.textSecondary, fontStyle: 'italic' }}>
            {(record.ticket.kilos_netos || 0).toFixed(2)}
          </span>
        ) : null;
      }
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Cant. Fardos</span>,
      dataIndex: 'total_cantidad_fardos',
      key: 'total_cantidad_fardos',
      width: 100,
      align: 'right' as const,
      render: (text, record) => record.isMain ? (
        <span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>{text || 0}</span>
      ) : null
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Peso Fardos</span>,
      dataIndex: 'total_peso_fardos',
      key: 'total_peso_fardos',
      width: 100,
      align: 'right' as const,
      render: (text, record) => record.isMain ? (
        <span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>{(text || 0).toFixed(2)}</span>
      ) : null
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Kg. Fibra</span>,
      dataIndex: 'kg_fibra',
      key: 'kg_fibra',
      width: 110,
      align: 'right' as const,
      render: (text: number, record: any) => record.isMain ? (
        <span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>
          {(text || 0).toFixed(2)}
        </span>
      ) : null
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Rinde Fibra %</span>,
      dataIndex: 'rinde_fibra',
      key: 'rinde_fibra',
      width: 110,
      align: 'right' as const,
      render: (text: number, record: any) => record.isMain ? (
        <span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>
          {(text || 0).toFixed(2)}%
        </span>
      ) : null
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Kilos Semilla</span>,
      dataIndex: 'kilos_semilla',
      key: 'kilos_semilla',
      width: 110,
      align: 'right' as const,
      render: (text: number, record: any) => record.isMain ? (
        <span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>
          {(text || 0).toFixed(2)}
        </span>
      ) : null
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Rinde Semilla %</span>,
      dataIndex: 'rinde_semilla',
      key: 'rinde_semilla',
      width: 120,
      align: 'right' as const,
      render: (text: number, record: any) => record.isMain ? (
        <span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>
          {(text || 0).toFixed(2)}%
        </span>
      ) : null
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Resto (+)</span>,
      dataIndex: 'resto_positivo',
      key: 'resto_positivo',
      width: 90,
      align: 'right' as const,
      render: (text: number, record: any) => record.isMain ? (
        <span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>
          {(text || 0).toFixed(2)}
        </span>
      ) : null
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Resto (-)</span>,
      dataIndex: 'resto_negativo',
      key: 'resto_negativo',
      width: 90,
      align: 'right' as const,
      render: (text: number, record: any) => record.isMain ? (
        <span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>
          {(text || 0).toFixed(2)}
        </span>
      ) : null
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Ritmo (Kg Fib/H)</span>,
      dataIndex: 'ritmo_desmote_kg_hora',
      key: 'ritmo_desmote_kg_hora',
      width: 130,
      align: 'right' as const,
      render: (text: number, record: any) => record.isMain ? (
        <span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>
          {(text || 0).toFixed(2)}
        </span>
      ) : null
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Ritmo (Q Fard/H)</span>,
      dataIndex: 'ritmo_desmote_q_hora',
      key: 'ritmo_desmote_q_hora',
      width: 130,
      align: 'right' as const,
      render: (text: number, record: any) => record.isMain ? (
        <span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>
          {(text || 0).toFixed(2)}
        </span>
      ) : null
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Acciones</span>,
      key: 'acciones',
      width: 150,
      align: 'center' as const,
      fixed: 'right' as const,
      render: (_, record) => {
        if (!record.isMain) return null;
        const tipoUsuario = (user as unknown as User)?.tipo_usuario;
        return (
          <Space size="small">
            <Button
              size="small"
              onClick={handleEmision}
              type="primary"
              style={{
                fontSize: '11px',
                height: '24px',
                padding: '0 8px'
              }}
            >
              Emisión
            </Button>
            {tipoUsuario === 'D' && (
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditarProduccion(record.id);
                }}
                style={{
                  backgroundColor: theme.colors.primary,
                  borderColor: theme.colors.primary,
                  color: '#fff',
                  height: '24px'
                }}
                title="Editar"
              />
            )}
          </Space>
        );
      }
    }
  ];

  // Columnas visibles, en el orden elegido por el usuario en el selector de columnas
  const filteredColumns = useMemo(() => {
    const columnsByKey = new Map(columns.map(col => [col.key as string, col]));
    return columnManager.orderedVisibleKeys
      .map(key => columnsByKey.get(key))
      .filter((col): col is (typeof columns)[number] => Boolean(col));
  }, [columnManager.orderedVisibleKeys, columns]);

  return (
    <App>
      <div style={{ padding: '0' }}>
        {/* Título y estadísticas compactas */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '12px',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <h1 style={{ 
              color: theme.colors.primaryDark, 
              fontSize: '22px', 
              fontWeight: 600, 
              margin: 0
            }}>
              Producción Desmote
            </h1>

            {/* Estadísticas compactas en la barra superior */}
            {resultados.length > 0 && (
              <div style={{ 
                display: 'flex', 
                gap: '8px', 
                alignItems: 'center',
                flexWrap: 'wrap'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '3px 8px',
                  backgroundColor: theme.colors.backgroundGray,
                  borderRadius: '4px',
                  border: `1px solid ${theme.colors.borderLight}`
                }}>
                  <DatabaseOutlined style={{ fontSize: '12px', color: theme.colors.primary }} />
                  <Text type="secondary" style={{ fontSize: '10px' }}>Total:</Text>
                  <Text strong style={{ fontSize: '12px', color: theme.colors.textPrimary }}>
                    {estadisticas.total}
                  </Text>
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '3px 8px',
                  backgroundColor: theme.colors.infoLight,
                  borderRadius: '4px',
                  border: `1px solid ${theme.colors.info}`
                }}>
                  <DatabaseOutlined style={{ fontSize: '12px', color: theme.colors.infoDark }} />
                  <Text type="secondary" style={{ fontSize: '10px' }}>Kilos:</Text>
                  <Text strong style={{ fontSize: '12px', color: theme.colors.infoDark }}>
                    {estadisticas.totalKilos.toLocaleString('es-AR', { maximumFractionDigits: 2 })}
                  </Text>
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '3px 8px',
                  backgroundColor: theme.colors.successLight,
                  borderRadius: '4px',
                  border: `1px solid ${theme.colors.success}`
                }}>
                  <CheckCircleOutlined style={{ fontSize: '12px', color: theme.colors.successDark }} />
                  <Text type="secondary" style={{ fontSize: '10px' }}>Fardos:</Text>
                  <Text strong style={{ fontSize: '12px', color: theme.colors.successDark }}>
                    {estadisticas.totalFardos}
                  </Text>
                </div>

                <Button
                  type="text"
                  size="small"
                  icon={showStatsExpanded ? <UpOutlined /> : <DownOutlined />}
                  onClick={() => setShowStatsExpanded(!showStatsExpanded)}
                  style={{
                    fontSize: '11px',
                    color: theme.colors.textSecondary,
                    height: '22px',
                    padding: '0 6px'
                  }}
                >
                  {showStatsExpanded ? 'Ocultar' : 'Ver más'}
                </Button>
              </div>
            )}
          </div>

          {/* Botón Nuevo a la derecha */}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleProduccionManual}
            size="small"
            style={{
              backgroundColor: theme.colors.secondary,
              borderColor: theme.colors.secondary,
              fontWeight: 500,
              height: '28px',
              borderRadius: theme.borderRadius.full
            }}
          >
            Nuevo
          </Button>
        </div>

        {/* Cards de resumen expandidas (solo visibles cuando showStatsExpanded es true) */}
        {showStatsExpanded && resultados.length > 0 && (
          <div style={{ 
            display: 'grid', 
            gap: '8px', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
            marginBottom: '12px'
          }}>
            <Card style={{ 
              borderTop: `4px solid ${theme.colors.primary}`, 
              minWidth: '150px', 
              flex: '1 1 auto', 
              height: '100%',
              backgroundColor: '#fff',
              borderRadius: theme.styles.borderRadius,
              boxShadow: theme.styles.boxShadowLight
            }} bodyStyle={{ padding: '8px', minHeight: '60px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text type="secondary" style={{ fontSize: '10px', display: 'block', wordBreak: 'break-word' }}>Total Producciones</Text>
                  <Title level={5} style={{ margin: '4px 0 0 0', color: theme.colors.textPrimary, wordBreak: 'break-word', overflowWrap: 'break-word', fontSize: '16px' }}>
                    {estadisticas.total.toLocaleString('es-AR')}
                  </Title>
                </div>
                <DatabaseOutlined style={{ fontSize: '20px', color: theme.colors.primary, flexShrink: 0 }} />
              </div>
            </Card>

            <Card style={{
              borderTop: `4px solid ${theme.chartPalette[2]}`,
              minWidth: '150px',
              flex: '1 1 auto',
              height: '100%',
              backgroundColor: '#fff',
              borderRadius: theme.styles.borderRadius,
              boxShadow: theme.styles.boxShadowLight
            }} bodyStyle={{ padding: '8px', minHeight: '60px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text type="secondary" style={{ fontSize: '10px', display: 'block', wordBreak: 'break-word' }}>Kilos Totales</Text>
                  <Title level={5} style={{ margin: '4px 0 0 0', color: theme.chartPalette[2], wordBreak: 'break-word', overflowWrap: 'break-word', fontSize: '16px' }}>
                    {estadisticas.totalKilos.toLocaleString('es-AR', { maximumFractionDigits: 2 })}
                  </Title>
                </div>
                <DatabaseOutlined style={{ fontSize: '20px', color: theme.chartPalette[2], flexShrink: 0 }} />
              </div>
            </Card>

            <Card style={{
              borderTop: `4px solid ${theme.chartPalette[4]}`,
              minWidth: '150px',
              flex: '1 1 auto',
              height: '100%',
              backgroundColor: '#fff',
              borderRadius: theme.styles.borderRadius,
              boxShadow: theme.styles.boxShadowLight
            }} bodyStyle={{ padding: '8px', minHeight: '60px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text type="secondary" style={{ fontSize: '10px', display: 'block', wordBreak: 'break-word' }}>Total Fardos</Text>
                  <Title level={5} style={{ margin: '4px 0 0 0', color: theme.chartPalette[4], wordBreak: 'break-word', overflowWrap: 'break-word', fontSize: '16px' }}>
                    {estadisticas.totalFardos.toLocaleString('es-AR')}
                  </Title>
                </div>
                <CheckCircleOutlined style={{ fontSize: '20px', color: theme.chartPalette[4], flexShrink: 0 }} />
              </div>
            </Card>

            <Card style={{
              borderTop: `4px solid ${theme.chartPalette[3]}`,
              minWidth: '150px',
              flex: '1 1 auto',
              height: '100%',
              backgroundColor: '#fff',
              borderRadius: theme.styles.borderRadius,
              boxShadow: theme.styles.boxShadowLight
            }} bodyStyle={{ padding: '8px', minHeight: '60px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text type="secondary" style={{ fontSize: '10px', display: 'block', wordBreak: 'break-word' }}>Peso Fardos</Text>
                  <Title level={5} style={{ margin: '4px 0 0 0', color: theme.chartPalette[3], wordBreak: 'break-word', overflowWrap: 'break-word', fontSize: '16px' }}>
                    {estadisticas.totalPesoFardos.toLocaleString('es-AR', { maximumFractionDigits: 2 })}
                  </Title>
                </div>
                <WarningOutlined style={{ fontSize: '20px', color: theme.chartPalette[3], flexShrink: 0 }} />
              </div>
            </Card>

            <Card style={{
              borderTop: `4px solid ${theme.chartPalette[5]}`,
              minWidth: '150px',
              flex: '1 1 auto',
              height: '100%',
              backgroundColor: '#fff',
              borderRadius: theme.styles.borderRadius,
              boxShadow: theme.styles.boxShadowLight
            }} bodyStyle={{ padding: '8px', minHeight: '60px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text type="secondary" style={{ fontSize: '10px', display: 'block', wordBreak: 'break-word' }}>Promedio Demora</Text>
                  <Title level={5} style={{ margin: '4px 0 0 0', color: theme.chartPalette[5], wordBreak: 'break-word', overflowWrap: 'break-word', fontSize: '16px' }}>
                    {estadisticas.promedioDemora} min
                  </Title>
                </div>
                <WarningOutlined style={{ fontSize: '20px', color: theme.chartPalette[5], flexShrink: 0 }} />
              </div>
            </Card>

            {estadisticas.topProductos.length > 0 && (
              <Card style={{ 
                borderTop: `4px solid ${theme.colors.secondary}`,
                backgroundColor: '#fff',
                borderRadius: theme.styles.borderRadius,
                boxShadow: theme.styles.boxShadowLight
              }} bodyStyle={{ padding: '8px', minHeight: '60px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <Text type="secondary" style={{ fontSize: '10px', display: 'block' }}>Top Producto</Text>
                    <Title level={5} style={{ margin: '4px 0 0 0', color: theme.colors.textPrimary, fontSize: '14px' }}>
                      {estadisticas.topProductos[0]?.nombre || 'N/A'}
                    </Title>
                    <Text style={{ fontSize: '10px', color: theme.colors.textSecondary }}>
                      {estadisticas.topProductos[0]?.count || 0} producciones
                    </Text>
                  </div>
                </div>
              </Card>
            )}
          </div>
        )}

      <Card 
        style={{ 
          backgroundColor: '#ffffff',
          borderRadius: theme.styles.borderRadius,
          boxShadow: theme.styles.boxShadow
        }}
        styles={{
          header: {
            backgroundColor: theme.colors.primaryDark,
            color: 'white',
            borderRadius: '8px 8px 0 0',
            padding: '8px 16px',
            fontSize: '14px'
          }
        }}
        title="Búsqueda de Producción"
      >
      <div style={{ display: 'flex', flexDirection: 'column' }}>
      {/* Filtros */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '12px',
        marginBottom: '12px',
        alignItems: 'end',
        rowGap: '12px'
      }}>
        {/* Select de Productos */}
        <div>
          <label style={{ 
            display: 'block', 
            marginBottom: '4px', 
            fontWeight: 600,
            color: theme.colors.textPrimary,
            fontSize: '12px'
          }}>
            Producto:
          </label>
          <Select
            value={productoSeleccionado || undefined}
            onChange={(value) => setProductoSeleccionado(value)}
            placeholder="Seleccionar producto"
            size="small"
            style={{ width: '100%' }}
          >
            {productos.map((producto) => (
              <Select.Option key={producto.id} value={producto.id.toString()}>
                {producto.descripcion}
              </Select.Option>
            ))}
          </Select>
        </div>

        {/* Select de Compra/Servicio */}
        <div>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Compra/Servicio:
          </label>
          <select
            value={filtroCompraServicio}
            onChange={(e) => setFiltroCompraServicio(e.target.value)}
            style={{
              padding: '8px 12px',
              border: `1px solid ${theme.colors.border}`,
              borderRadius: '6px',
              minWidth: '150px'
            }}
          >
            <option value="">Todos</option>
            <option value="Compra">Compra</option>
            <option value="Servicio">Servicio</option>
          </select>
        </div>

        {/* Fecha Desde */}
        <div>
          <label style={{ 
            display: 'block', 
            marginBottom: '4px', 
            fontWeight: 600,
            color: theme.colors.textPrimary,
            fontSize: '12px'
          }}>
            Desde:
          </label>
          <DatePicker
            value={fechaDesde ? dayjs(fechaDesde) : null}
            onChange={(date: Dayjs | null) => setFechaDesde(date ? date.format('YYYY-MM-DD') : '')}
            size="small"
            style={{ width: '100%' }}
            format="DD/MM/YYYY"
          />
        </div>

        {/* Fecha Hasta */}
        <div>
          <label style={{ 
            display: 'block', 
            marginBottom: '4px', 
            fontWeight: 600,
            color: theme.colors.textPrimary,
            fontSize: '12px'
          }}>
            Hasta:
          </label>
          <DatePicker
            value={fechaHasta ? dayjs(fechaHasta) : null}
            onChange={(date: Dayjs | null) => setFechaHasta(date ? date.format('YYYY-MM-DD') : '')}
            size="small"
            style={{ width: '100%' }}
            format="DD/MM/YYYY"
          />
        </div>

        {/* Botón Búsqueda */}
        <div>
          <Button
            type="primary"
            onClick={handleBusqueda}
            size="small"
            loading={loading}
            style={{
              backgroundColor: theme.colors.primary,
              borderColor: theme.colors.primary,
              fontWeight: 500,
              width: '100%',
              height: '28px',
              borderRadius: theme.borderRadius.full
            }}
          >
            Buscar
          </Button>
        </div>
      </div>

      {/* Tabla de resultados */}
      {!loading && resultados.length > 0 && (
        <div style={{ marginTop: '12px' }}>
          {/* Barra de búsqueda y acciones */}
          <div style={{ marginBottom: '12px', display: 'flex', gap: '8px', justifyContent: 'space-between', flexWrap: 'wrap', alignItems: 'center' }}>
            <Input
              placeholder="Buscar en todos los campos..."
              prefix={<SearchOutlined style={{ color: theme.colors.textSecondary, fontSize: '12px' }} />}
              value={globalSearch}
              onChange={(e) => setGlobalSearch(e.target.value)}
              allowClear
              style={{
                maxWidth: '350px',
                flex: 1,
                minWidth: '200px',
                fontSize: '12px',
                borderRadius: theme.borderRadius.full
              }}
              size="small"
            />
            <Space size="small">
              <ColumnManagerDropdown
                manager={columnManager}
                labels={columnNames}
                requiredKeys={['acciones']}
                buttonSize="small"
              />
              <ExportExcelButton
                onClick={handleExportExcel}
                size="small"
                disabled={filteredData.length === 0}
              />
              <ExportPDFButton
                onClick={handleExportPDF}
                size="small"
                disabled={filteredData.length === 0}
              />
            </Space>
          </div>

          {/* Mensaje de resultados filtrados */}
          {globalSearch && (
            <div style={{ 
              marginBottom: '8px', 
              padding: '6px 10px', 
              backgroundColor: theme.colors.backgroundGray,
              borderRadius: theme.styles.borderRadius,
              fontSize: '12px',
              color: theme.colors.textSecondary
            }}>
              Mostrando {filteredData.length} de {resultados.length} producciones
              {globalSearch && ` que coinciden con "${globalSearch}"`}
            </div>
          )}

          <Table
            columns={filteredColumns}
            dataSource={paginatedData}
            loading={loading}
            rowKey="key"
            pagination={{
              current: currentPage,
              total: filteredData.length,
              pageSize,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} producciones`,
              onChange: handlePageChange,
              onShowSizeChange: handlePageChange,
              size: 'small'
            }}
            scroll={{ x: 'max-content', y: 'max(150px, calc(100vh - 350px))' }}
            size="middle"
            onRow={(record) => ({
              onClick: () => {
                if (record.isMain) {
                  handleSeleccionProduccion(record.id);
                }
              },
              onMouseEnter: (e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = record.isMain ? '#F5F8FB' : '#f0f0f0';
              },
              onMouseLeave: (e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = '';
              },
              style: {
                transition: 'background-color 0.2s ease',
                backgroundColor: record.isMain ? (produccionSeleccionada === record.id ? theme.colors.infoLight : 'white') : '#fafafa',
                cursor: record.isMain ? 'pointer' : 'default'
              }
            })}
          />
        </div>
      )}
      
      {!loading && resultados.length === 0 && (
        <div style={{ 
          textAlign: 'center', 
          color: theme.colors.textSecondary,
          padding: '30px',
          margin: 0,
          fontSize: '13px'
        }}>
          No se encontraron resultados para los criterios seleccionados
        </div>
      )}

      {/* Cards de acción clickeables */}
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '12px',
        marginTop: '16px',
        paddingTop: '16px',
        borderTop: `1px solid ${theme.colors.borderLight}`
      }}>
        <Card
          hoverable
          onClick={handleProduccionManual}
          style={{
            cursor: 'pointer',
            borderRadius: theme.styles.borderRadius,
            border: `1px solid ${theme.colors.borderLight}`,
            transition: 'all 0.3s ease',
            backgroundColor: '#fff'
          }}
          bodyStyle={{
            padding: '12px 16px',
            textAlign: 'center'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = theme.colors.secondary;
            e.currentTarget.style.boxShadow = `0 2px 8px rgba(82, 196, 26, 0.15)`;
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = theme.colors.borderLight;
            e.currentTarget.style.boxShadow = 'none';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <div style={{ 
            fontSize: '15px', 
            fontWeight: 600, 
            color: theme.colors.textPrimary,
            marginBottom: '4px'
          }}>
            Producción Manual
          </div>
          <div style={{ 
            fontSize: '12px', 
            color: theme.colors.textSecondary,
            lineHeight: '1.4'
          }}>
            Selección manual de tickets
          </div>
        </Card>

        <Card
          hoverable
          onClick={handleProduccionAutomatica}
          style={{
            cursor: 'pointer',
            borderRadius: theme.styles.borderRadius,
            border: `1px solid ${theme.colors.borderLight}`,
            transition: 'all 0.3s ease',
            backgroundColor: '#fff'
          }}
          bodyStyle={{
            padding: '12px 16px',
            textAlign: 'center'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = theme.colors.secondary;
            e.currentTarget.style.boxShadow = `0 2px 8px rgba(82, 196, 26, 0.15)`;
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = theme.colors.borderLight;
            e.currentTarget.style.boxShadow = 'none';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <div style={{ 
            fontSize: '15px', 
            fontWeight: 600, 
            color: theme.colors.textPrimary,
            marginBottom: '4px'
          }}>
            Producción Automática
          </div>
          <div style={{ 
            fontSize: '12px', 
            color: theme.colors.textSecondary,
            lineHeight: '1.4'
          }}>
            Proceso automatizado
          </div>
        </Card>
      </div>
      </div>
      </Card>
    </div>
    </App>
  );
};

export default ListadoProduccion;
